import { motion } from "framer-motion";
import profilePhoto from "../assets/image.png";
import { useTranslation } from "react-i18next";
import { ArrowRight, Mail, Github, Sparkles, MapPin } from "lucide-react";

export default function Home() {
  const { t } = useTranslation();

  return (
    <section
      id="home"
      className="relative min-h-[90vh] flex items-center justify-center px-6 sm:px-12 lg:px-20 py-20 bg-white dark:bg-black text-neutral-900 dark:text-white overflow-hidden"
    >
      {/* Subtle modern background glow */}
      <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-blue-500/[0.04] dark:bg-blue-500/[0.06] rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="max-w-5xl w-full flex flex-col-reverse lg:flex-row items-center justify-between gap-12 lg:gap-16 z-10">

        {/* Left Column: Typography & CTAs */}
        <motion.div
          className="flex-1 text-center lg:text-left"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide bg-neutral-100 dark:bg-neutral-900/90 border border-neutral-200/80 dark:border-neutral-800 text-neutral-700 dark:text-neutral-300 shadow-sm mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span>{t("home.status")}</span>
          </div>

          {/* Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-neutral-900 dark:text-white leading-[1.15]">
            {t("home.greeting")}{" "}
            <span className="relative inline-block text-neutral-900 dark:text-white">
              {t("home.name")}
              <span className="inline-block text-blue-500 ml-1">.</span>
            </span>
          </h1>

          {/* Subtitle / Role */}
          <p className="mt-3 text-lg sm:text-xl font-semibold text-neutral-700 dark:text-neutral-300 flex items-center justify-center lg:justify-start gap-2">
            <Sparkles size={18} className="text-blue-500 shrink-0" />
            <span>{t("home.role")}</span>
          </p>

          {/* Bio Description */}
          <p className="mt-4 text-base sm:text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-xl mx-auto lg:mx-0">
            {t("home.bio")}
          </p>

          {/* Call to Action Buttons */}
          <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-3.5">
            <a
              href="#projects"
              className="group inline-flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-semibold 
                         bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 
                         hover:bg-neutral-800 dark:hover:bg-neutral-100 
                         shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98]"
            >
              <span>{t("home.viewWork")}</span>
              <ArrowRight
                size={16}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </a>

            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold 
                         border border-neutral-300/80 dark:border-neutral-800 
                         bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-900/80 
                         text-neutral-800 dark:text-neutral-200 transition-all duration-200 active:scale-[0.98]"
            >
              <Mail size={16} className="text-neutral-500 dark:text-neutral-400" />
              <span>{t("home.getInTouch")}</span>
            </a>

            <a
              href="https://github.com/Rangsey0"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
              className="inline-flex items-center justify-center w-11 h-11 rounded-xl 
                         border border-neutral-300/80 dark:border-neutral-800 
                         bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-900/80 
                         text-neutral-700 dark:text-neutral-300 transition-all duration-200 active:scale-[0.98]"
            >
              <Github size={18} />
            </a>
          </div>

          {/* Quick info row */}
          <div className="mt-10 pt-6 border-t border-neutral-200/60 dark:border-neutral-800/60 flex items-center justify-center lg:justify-start gap-6 text-xs text-neutral-500 dark:text-neutral-400 font-medium">
            <div className="flex items-center gap-1.5">
              <MapPin size={14} className="text-neutral-400" />
              <span>Phnom Penh, Cambodia</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-neutral-300 dark:bg-neutral-700" />
          </div>
        </motion.div>

        {/* Right Column: Refined Minimal Profile Photo Frame */}
        <motion.div
          className="shrink-0 relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        >
          <div className="relative w-64 h-72 sm:w-72 sm:h-80 md:w-80 md:h-96">
            {/* Ambient backlight ring */}
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-b from-neutral-200 to-neutral-100 dark:from-neutral-800 dark:to-neutral-900/20 opacity-70 blur-sm pointer-events-none" />

            {/* Inner Frame */}
            <div className="relative h-full w-full rounded-2xl overflow-hidden p-1.5 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200/90 dark:border-neutral-800 shadow-xl shadow-neutral-950/5 dark:shadow-black/50">
              <img
                src={profilePhoto}
                alt="Rangsey"
                className="w-full h-full object-cover rounded-xl filter contrast-[1.02]"
                loading="eager"
              />

              {/* Minimal floating pill on photo */}
              <div className="absolute bottom-3 left-3 right-3 py-2 px-3 rounded-xl bg-neutral-900/80 dark:bg-black/80 backdrop-blur-md border border-white/10 text-white flex items-center justify-between text-xs">
                <span className="font-semibold tracking-wide">Rangsey</span>
                <span className="text-[11px] text-neutral-400 font-mono">Web Dev</span>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
